import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { AttachmentUrlResponse, ChatDetail, ChatSummary, ListChatsResponse } from '@medicail/shared';
import { apiFetch, errorMessage } from '@/lib/http';
import { toast } from 'sonner';
import { getPreview } from '@/lib/previewCache';

export const chatKeys = {
  list: ['chats'] as const,
  detail: (id: string) => ['chat', id] as const,
};

export function useChats() {
  return useQuery({
    queryKey: chatKeys.list,
    queryFn: async () => (await apiFetch<ListChatsResponse>('/chats')).chats,
    staleTime: 30_000,
  });
}

export function useChat(chatId: string | undefined) {
  return useQuery({
    queryKey: chatKeys.detail(chatId ?? ''),
    queryFn: () => apiFetch<ChatDetail>(`/chats/${chatId}`),
    enabled: Boolean(chatId),
    staleTime: 30_000,
  });
}

/** Signed URL for an image attachment (falls back to the local object URL right after upload). */
export function useAttachmentUrl(attachmentId: string, enabled = true) {
  const local = getPreview(attachmentId);
  return useQuery({
    queryKey: ['attachment-url', attachmentId],
    queryFn: async () => (await apiFetch<AttachmentUrlResponse>(`/uploads/${attachmentId}`)).url,
    enabled: enabled && !local,
    staleTime: 4 * 60_000,
    retry: false,
  }).data ?? local;
}

export function addChatToCache(qc: QueryClient, chat: ChatSummary) {
  qc.setQueryData<ChatSummary[]>(chatKeys.list, (old) => [chat, ...(old ?? []).filter((c) => c.chatId !== chat.chatId)]);
  qc.setQueryData<ChatDetail>(chatKeys.detail(chat.chatId), { ...chat, messages: [] });
}

export async function createChat(qc: QueryClient): Promise<ChatSummary> {
  const chat = await apiFetch<ChatSummary>('/chats', { method: 'POST', json: {} });
  addChatToCache(qc, chat);
  return chat;
}

export function useRenameChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ chatId, title }: { chatId: string; title: string }) =>
      apiFetch<ChatSummary>(`/chats/${chatId}`, { method: 'PATCH', json: { title } }),
    onMutate: async ({ chatId, title }) => {
      await qc.cancelQueries({ queryKey: chatKeys.list });
      const prev = qc.getQueryData<ChatSummary[]>(chatKeys.list);
      qc.setQueryData<ChatSummary[]>(chatKeys.list, (old) => old?.map((c) => (c.chatId === chatId ? { ...c, title } : c)));
      qc.setQueryData<ChatDetail>(chatKeys.detail(chatId), (old) => (old ? { ...old, title } : old));
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(chatKeys.list, ctx.prev);
      toast.error(errorMessage(err, 'Could not rename the chat.'));
    },
    onSettled: () => qc.invalidateQueries({ queryKey: chatKeys.list }),
  });
}

export function useDeleteChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (chatId: string) => apiFetch<void>(`/chats/${chatId}`, { method: 'DELETE' }),
    onMutate: async (chatId) => {
      await qc.cancelQueries({ queryKey: chatKeys.list });
      const prev = qc.getQueryData<ChatSummary[]>(chatKeys.list);
      qc.setQueryData<ChatSummary[]>(chatKeys.list, (old) => old?.filter((c) => c.chatId !== chatId));
      return { prev };
    },
    onError: (err, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(chatKeys.list, ctx.prev);
      toast.error(errorMessage(err, 'Could not delete the chat.'));
    },
    onSuccess: (_d, chatId) => qc.removeQueries({ queryKey: chatKeys.detail(chatId) }),
    onSettled: () => qc.invalidateQueries({ queryKey: chatKeys.list }),
  });
}

export function useDeleteAllChats() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<void>('/chats', { method: 'DELETE' }),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: chatKeys.list });
      const prev = qc.getQueryData<ChatSummary[]>(chatKeys.list);
      qc.setQueryData<ChatSummary[]>(chatKeys.list, []);
      return { prev };
    },
    onError: (err, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(chatKeys.list, ctx.prev);
      toast.error(errorMessage(err, 'Could not delete your chats.'));
    },
    onSuccess: () => {
      qc.removeQueries({ queryKey: ['chat'] });
      toast.success('All chats deleted');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: chatKeys.list }),
  });
}
